import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatOption, MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { DiseaseChartDay } from '@app/shared/components/disease-insight-chart/models';
import {
  CropFieldAdvice,
  VirusMonitorCropFieldRegistration,
  VirusSprayCategory,
} from '@app/shared/components/disease-overview-table.component/models/models';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import { defaultBlightDayRange } from 'src/app/blight-v2/shared/helpers/blight-helpers';
import { DateSelectorComponent } from 'src/app/shared/components/date-selector.component/date-selector.component';
import { DiseaseInsightChartComponent } from 'src/app/shared/components/disease-insight-chart/disease-insight-chart.component';
import { CHART_CONFIG } from 'src/app/shared/components/disease-insight-chart/base-chart.config';
import { VirusMonitorChartConfig } from 'src/app/virus-monitor/shared/virus-monitor-chart.config';
import { RecalculateButtonComponent } from 'src/app/shared/components/recalculate-button/recalculate-button.component';
import {
  mapVirusMonitorDataToCropFieldRegistration,
  mapVirusMonitorDaysToChartDays,
} from 'src/app/virus-monitor/models/mappers';
import { VirusMonitorActionMenuComponent } from 'src/app/virus-monitor/shared/action-menu.component';
import { VirusMonitorSignalStore } from 'src/app/virus-monitor/signal-store/virus-monitor-signal-store';
import { createVirusMonitorTelemetryProperties, VirusMonitorAIIdentifiers } from '../../../../shared/constants';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, filter, of, switchMap } from 'rxjs';
import { DateService } from '@app/shared/services/date.service';
import { WeatherService } from 'src/app/tga/services/weather.service';
import { VirusMonitorService } from 'src/app/virus-monitor/services/virus-monitor.service';
import { VirusMonitorInsightToolbarComponent } from './components/virus-monitor-insight-toolbar/virus-monitor-insight-toolbar.component';
import {
  ProtectionTypeDetail,
  VirusMonitorProtectionTypeSelectorComponent,
} from './components/virus-monitor-protection-type-selector/virus-monitor-protection-type-selector.component';
import { getProtectionLevelStyling, getUvIndexStyling } from 'src/app/virus-monitor/shared/helpers/advice-style.helper';
import { DecimalPipe } from '@angular/common';
import { longestCropFieldText } from 'src/app/shared/utils/panel-min-width.utils';
import { VirusMonitorDay } from 'src/app/virus-monitor/models/models';

type VirusMonitorTooltipType =
  | 'combinedProtection'
  | 'oilProtection'
  | 'pyrethroidProtection'
  | 'systemicProtection'
  | 'uvIndex';

@Component({
  selector: 'app-virus-monitor-insight-page',
  templateUrl: './virus-monitor-insight-page.component.html',
  styleUrl: './virus-monitor-insight-page.component.scss',
  providers: [{ provide: CHART_CONFIG, useClass: VirusMonitorChartConfig }],
  imports: [
    RecalculateButtonComponent,
    DateSelectorComponent,
    TranslatePipe,
    ReactiveFormsModule,
    VirusMonitorInsightToolbarComponent,
    VirusMonitorActionMenuComponent,
    MatSelect,
    MatOption,
    MatButton,
    MatMenuTrigger,
    MatIcon,
    DiseaseInsightChartComponent,
    DecimalPipe,
    VirusMonitorProtectionTypeSelectorComponent,
  ],
})
export class VirusMonitorInsightPageComponent {
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(VirusMonitorSignalStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dateService = inject(DateService);
  private readonly localeService = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly weatherService = inject(WeatherService);
  private readonly virusMonitorService = inject(VirusMonitorService);
  private readonly ai = inject(AppInsightsServiceInjector);
  private readonly weatherForecast = toSignal(
    this.weatherService.getWeatherForecast().pipe(catchError(() => of(null))),
  );
  private readonly fullSeasonVirusMonitorData = toSignal(
    toObservable(
      computed(() => {
        const selectedVirusMonitorData = this.store.selectedVirusMonitorData();

        if (!selectedVirusMonitorData) {
          return undefined;
        }

        return {
          cropFieldId: selectedVirusMonitorData.cropFieldId,
          cropYear: selectedVirusMonitorData.cropYear,
          customerId: this.store.customer()?.id,
        };
      }),
    ).pipe(
      distinctUntilChanged(
        (left, right) =>
          left?.cropFieldId === right?.cropFieldId &&
          left?.cropYear === right?.cropYear &&
          left?.customerId === right?.customerId,
      ),
      switchMap((query) => {
        if (!query) {
          return of(undefined);
        }

        return this.virusMonitorService
          .getAdvisory(query.cropFieldId, query.cropYear, query.customerId)
          .pipe(catchError(() => of(undefined)));
      }),
    ),
  );

  disableCropFieldSelector = input(false);

  protected readonly cropFields = linkedSignal<CropFieldAdvice[] | undefined, CropFieldAdvice[]>({
    source: () => {
      if (this.store.virusMonitorRegistrationsIsLoading()) return undefined;
      return this.store.virusMonitorAdvices();
    },
    computation: (source, previous) => source ?? previous?.value ?? [],
  });

  protected readonly longestOptionText = computed(() => longestCropFieldText(this.cropFields()));

  protected readonly minimalDate = this.store.virusMonitorMinAdviceDate;
  protected readonly selectedCropFieldAdvice = this.store.selectedCropFieldAdvice;
  protected readonly hasAdvices = this.store.hasSelectedCropFieldAdvices;

  protected readonly maximalDate = computed(() =>
    this.dateService.daysInTheFuture(defaultBlightDayRange.daysInFuture, this.store.cropYear()),
  );
  protected readonly isReadOnly = computed(() => !this.selectedCropField() || this.isCropYearReadOnly());
  protected readonly isRecalculationDisabled = linkedSignal<boolean | undefined, boolean>({
    source: () => {
      if (this.store.virusMonitorRegistrationsIsLoading()) return undefined;
      return (
        this.store.isVirusMonitorCropFieldsSyncing() ||
        this.isReadOnly() ||
        this.store.isSelectedCropFieldIneligibleForRecalculation()
      );
    },
    computation: (source, previous) => source ?? previous?.value ?? true,
  });

  protected readonly selectedCropField = linkedSignal<
    VirusMonitorCropFieldRegistration | undefined,
    VirusMonitorCropFieldRegistration | undefined
  >({
    source: () => {
      if (this.store.virusMonitorRegistrationsIsLoading()) return undefined;
      const data = this.store.selectedVirusMonitorData();
      return data ? mapVirusMonitorDataToCropFieldRegistration(data) : undefined;
    },
    computation: (source, previous) => source ?? previous?.value,
  });

  protected readonly chartDays = linkedSignal<DiseaseChartDay[] | undefined, DiseaseChartDay[]>({
    source: () => {
      if (this.store.virusMonitorRegistrationsIsLoading()) return undefined;
      const data = this.store.selectedVirusMonitorData();
      const forecast = this.weatherForecast();
      const chartDays = data
        ? mapVirusMonitorDaysToChartDays(data.virusMonitorResult.days, data.irrigations)
        : undefined;
      if (chartDays && forecast) {
        return chartDays.map((day) => {
          const matchingForecastDay = forecast.days.find((fd) => {
            const forecastDate = new Date(fd.date);
            return (
              forecastDate.getUTCFullYear() === day.date.getUTCFullYear() &&
              forecastDate.getUTCMonth() === day.date.getUTCMonth() &&
              forecastDate.getUTCDate() === day.date.getUTCDate()
            );
          });
          return matchingForecastDay?.uvIndex == null ? day : { ...day, uvIndex: matchingForecastDay.uvIndex };
        });
      }
      return chartDays ?? undefined;
    },
    computation: (source, previous) => source ?? previous?.value ?? [],
  });

  protected readonly adviceForChart = linkedSignal<CropFieldAdvice | undefined, CropFieldAdvice | undefined>({
    source: () => {
      if (this.store.virusMonitorRegistrationsIsLoading()) return undefined;
      return this.store.selectedCropFieldAdvice();
    },
    computation: (source, previous) => source ?? previous?.value,
  });

  protected readonly selectedAdvice = computed((): CropFieldAdvice[] => {
    const advice = this.store.selectedCropFieldAdvice();
    return advice ? [advice] : [];
  });
  protected readonly selectedDates = computed((): Date[] =>
    this.store.fromDate() || this.store.toDate() ? [this.store.fromDate(), this.store.toDate()] : [],
  );
  protected readonly warningProtectionTypes = computed((): VirusSprayCategory[] => {
    const selectedVirusMonitorData = this.store.selectedVirusMonitorData();
    const today = selectedVirusMonitorData?.virusMonitorResult.today;

    if (!selectedVirusMonitorData || !today) {
      return [];
    }

    return selectedVirusMonitorData.selectedProtectionTypes.filter(
      (protectionType) => getProtectionPercentageForType(today, protectionType) <= 0,
    );
  });
  protected readonly protectionTypeDetails = computed((): Record<VirusSprayCategory, ProtectionTypeDetail> => {
    const selectedVirusMonitorData = this.store.selectedVirusMonitorData();
    const fullSeasonVirusMonitorData = this.fullSeasonVirusMonitorData();
    const detailSource = fullSeasonVirusMonitorData ?? selectedVirusMonitorData;
    const today = selectedVirusMonitorData?.virusMonitorResult.today;
    const details = {} as Record<VirusSprayCategory, ProtectionTypeDetail>;

    for (const protectionType of [VirusSprayCategory.Systemic, VirusSprayCategory.Pyrethroid, VirusSprayCategory.Oil]) {
      details[protectionType] = getProtectionTypeDetail(
        protectionType,
        detailSource?.virusMonitorResult.days ?? [],
        today,
        selectedVirusMonitorData?.availableProtectionTypes ?? [],
        !!detailSource,
      );
    }

    return details;
  });

  protected readonly actionDate = signal<Date | undefined>(undefined);

  @ViewChild('menuTrigger', { read: MatMenuTrigger }) private readonly menuTrigger!: MatMenuTrigger;
  @ViewChild('menuTrigger', { read: ElementRef }) private readonly menuTriggerEl!: ElementRef<HTMLElement>;

  protected getProtectionLevelStyling(protectionLevelPercentage: number): { [key: string]: string } {
    return getProtectionLevelStyling(protectionLevelPercentage);
  }

  protected getUvIndexStyling(uvIndex: number | undefined): { [key: string]: string } {
    return getUvIndexStyling(uvIndex) ?? {};
  }

  protected readonly tooltipFormatter = (day: DiseaseChartDay, type: VirusMonitorTooltipType): string => {
    const config: Record<VirusMonitorTooltipType, { key: string; value: number | undefined; unit: string }> = {
      combinedProtection: {
        key: 'virusMonitor.riskChart.combinedProtection',
        value: day.overallProtectionPercent,
        unit: '%',
      },
      oilProtection: { key: 'virusMonitor.riskChart.oilProtection', value: day.oilProtectionPercent, unit: '%' },
      pyrethroidProtection: {
        key: 'virusMonitor.riskChart.pyrethroidProtection',
        value: day.pyrethroidProtectionPercent,
        unit: '%',
      },
      systemicProtection: {
        key: 'virusMonitor.riskChart.systemicProtection',
        value: day.systemicProtectionPercent,
        unit: '%',
      },
      uvIndex: { key: 'virusMonitor.riskChart.uvIndex', value: day.uvIndex, unit: '' },
    };
    const entry = config[type];
    if (!entry) {
      return '';
    }
    const label = this.localeService.translate(entry.key);
    const formattedValue = entry.value == null ? '-' : `${Math.round(entry.value)}${entry.unit}`;
    return `${label}: ${formattedValue}`;
  };

  protected form = new FormGroup({
    cropField: new FormControl(),
  });

  constructor() {
    this.initializeCropFieldSelection();
    this.subscribeToCropFieldChange();
  }

  protected onSelectedDatesChanged(dates: Date[]): void {
    this.store.patchSelectedDates(dates);
  }

  protected onRecalculate(): void {
    const cropField = this.selectedCropField();
    if (cropField && !this.isReadOnly()) {
      this.ai.trackEvent(
        VirusMonitorAIIdentifiers.insightsCalculateClicked,
        createVirusMonitorTelemetryProperties('insightsCalculateClicked', {
          cropFieldId: cropField.cropFieldId,
        }),
      );
      this.store.recalculateVirusMonitor([cropField.cropFieldId]);
    }
  }

  protected onPlantDateChanged(date: Date): void {
    const cropField = this.selectedCropField();
    if (!cropField || this.isReadOnly()) {
      return;
    }

    this.store.updatePlantDate({
      date,
      cropFieldId: cropField.cropFieldId,
      year: cropField.year,
    });
  }

  protected onEmergenceDateChanged(date: Date): void {
    const cropField = this.selectedCropField();
    if (!cropField || this.isReadOnly()) {
      return;
    }

    this.store.updateEmergenceDate({
      date,
      cropFieldId: cropField.cropFieldId,
      year: cropField.year,
    });
  }

  protected onHarvestDateChanged(date: Date): void {
    const cropField = this.selectedCropField();
    if (!cropField || this.isReadOnly()) {
      return;
    }

    this.store.updateHarvestDate({
      date,
      cropFieldId: cropField.cropFieldId,
      year: cropField.year,
    });
  }

  protected onDeadFoliageDateChanged(date: Date): void {
    const cropField = this.selectedCropField();
    if (cropField && !this.isReadOnly()) {
      this.store.updateDeadFoliageDate({ date, cropFieldId: cropField.cropFieldId });
    }
  }

  protected onSprayThresholdChanged(sprayThreshold: number): void {
    const cropField = this.selectedCropField();
    if (cropField && !this.isReadOnly()) {
      this.store.updateSprayThreshold({ sprayThreshold, cropFieldId: cropField.cropFieldId });
    }
  }

  protected onProtectionTypesChanged(selectedProtectionTypes: VirusSprayCategory[]): void {
    const cropField = this.selectedCropField();
    if (cropField && !this.isReadOnly()) {
      this.store.updateProtectionTypes({ selectedProtectionTypes, cropFieldId: cropField.cropFieldId });
    }
  }

  protected onActionTriggered({ event, date }: { event: MouseEvent; date: Date }): void {
    this.actionDate.set(date);
    const el = this.menuTriggerEl.nativeElement;
    el.style.left = `${event.clientX + 10}px`;
    el.style.top = `${event.clientY + 10}px`;
    this.menuTrigger.openMenu();
  }

  protected onActionMenuOpened(source: 'button' | 'chart' = 'button'): void {
    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.insightsActionMenuOpened,
      createVirusMonitorTelemetryProperties('insightsActionMenuOpened', {
        actionType: source,
      }),
    );
  }

  protected onGraphSeriesToggled({ seriesName, visible }: { seriesName: string; visible: boolean }): void {
    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.insightsGraphSeriesToggled,
      createVirusMonitorTelemetryProperties('insightsGraphSeriesToggled', {
        seriesName,
        isVisible: visible,
      }),
    );
  }

  private initializeCropFieldSelection(): void {
    effect(() => {
      const cropFields = this.cropFields();
      if (!cropFields?.length) {
        return;
      }

      const currentSelected = this.store.selectedCropFieldId();

      // If a crop field is already selected and still exists in the list,
      // just ensure the form control is in sync
      if (currentSelected && cropFields.some((cf) => cf.cropFieldId === currentSelected)) {
        const currentFormValue = this.form.controls.cropField.value;
        if (currentFormValue !== currentSelected) {
          untracked(() => {
            this.form.controls.cropField.setValue(currentSelected, { emitEvent: false });
            this.router.navigate([], {
              queryParams: { cropFieldId: currentSelected },
              queryParamsHandling: 'merge',
            });
          });
        }
        return;
      }

      // Fall back to query param or first available crop field
      const cropFieldQueryParam = this.route.snapshot.queryParamMap.get('cropFieldId');
      const queryParamCropFieldId = cropFieldQueryParam ? +cropFieldQueryParam : undefined;

      const targetId = queryParamCropFieldId
        ? (cropFields.find((cf) => cf.cropFieldId === queryParamCropFieldId)?.cropFieldId ?? cropFields[0]?.cropFieldId)
        : cropFields[0]?.cropFieldId;

      if (targetId) {
        untracked(() => {
          this.store.selectCropField(targetId);
          this.form.controls.cropField.setValue(targetId, { emitEvent: false });
          this.router.navigate([], {
            queryParams: { cropFieldId: targetId },
            queryParamsHandling: 'merge',
          });
        });
      }
    });
  }

  private subscribeToCropFieldChange(): void {
    this.form.controls.cropField.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((cropFieldId) => !!cropFieldId),
        distinctUntilChanged(),
      )
      .subscribe((cropFieldId) => {
        this.ai.trackEvent(
          VirusMonitorAIIdentifiers.insightsCropFieldSelected,
          createVirusMonitorTelemetryProperties('insightsCropFieldSelected', {
            cropFieldId: +cropFieldId,
          }),
        );
        this.store.selectCropField(+cropFieldId);
        this.router.navigate([], {
          queryParams: { cropFieldId },
          queryParamsHandling: 'merge',
        });
      });
  }
}

function getProtectionPercentageForType(
  day:
    | Pick<VirusMonitorDay, 'oilProtectionPercent' | 'pyrethroidProtectionPercent' | 'systemicProtectionPercent'>
    | undefined,
  protectionType: VirusSprayCategory,
): number {
  if (!day) {
    return 0;
  }

  switch (protectionType) {
    case VirusSprayCategory.Oil:
      return day.oilProtectionPercent ?? 0;
    case VirusSprayCategory.Pyrethroid:
      return day.pyrethroidProtectionPercent ?? 0;
    case VirusSprayCategory.Systemic:
      return day.systemicProtectionPercent ?? 0;
  }
}

function getProtectionTypeDetail(
  protectionType: VirusSprayCategory,
  days: VirusMonitorDay[],
  today: VirusMonitorDay | undefined,
  availableProtectionTypes: VirusSprayCategory[],
  hasReliablePeriodData: boolean,
): ProtectionTypeDetail {
  if (!availableProtectionTypes.includes(protectionType)) {
    return { status: 'unused' };
  }

  const todayDate = startOfDay(today?.date ?? new Date());
  const todayProtectionPercentage = getProtectionPercentageForType(today, protectionType);

  if (!hasReliablePeriodData) {
    return {
      status: todayProtectionPercentage > 0 ? 'active' : 'expired',
    };
  }

  const sortedDays = [...days]
    .filter((day) => getProtectionPercentageForType(day, protectionType) > 0)
    .sort((left, right) => left.date.getTime() - right.date.getTime());
  const protectsUntil = sortedDays.at(-1)?.date;

  if (!protectsUntil) {
    return { status: 'expired' };
  }

  const latestPeriod = getLatestProtectionPeriod(sortedDays);
  const remainingDays = latestPeriod.filter((day) => startOfDay(day.date) >= todayDate).length;

  return {
    status: todayProtectionPercentage > 0 ? 'active' : 'expired',
    latestAppliedOn: getLatestProtectionRenewalDate(latestPeriod, protectionType),
    protectsUntil,
    protectingDays: latestPeriod.length,
    remainingDays,
    daysSinceProtected: Math.max(daysBetween(protectsUntil, todayDate), 0),
  };
}

function getLatestProtectionRenewalDate(
  latestPeriod: VirusMonitorDay[],
  protectionType: VirusSprayCategory,
): Date | undefined {
  let latestRenewalDate = latestPeriod[0]?.date;

  for (let index = 1; index < latestPeriod.length; index++) {
    const previousProtectionPercentage = getProtectionPercentageForType(latestPeriod[index - 1], protectionType);
    const protectionPercentage = getProtectionPercentageForType(latestPeriod[index], protectionType);

    if (protectionPercentage >= previousProtectionPercentage) {
      latestRenewalDate = latestPeriod[index].date;
    }
  }

  return latestRenewalDate;
}

function getLatestProtectionPeriod(sortedProtectedDays: VirusMonitorDay[]): VirusMonitorDay[] {
  const period: VirusMonitorDay[] = [];

  for (let index = sortedProtectedDays.length - 1; index >= 0; index--) {
    const day = sortedProtectedDays[index];
    const nextDay = period[0];

    if (nextDay && daysBetween(day.date, nextDay.date) !== 1) {
      break;
    }

    period.unshift(day);
  }

  return period;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysBetween(left: Date, right: Date): number {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;

  return Math.round((startOfDay(right).getTime() - startOfDay(left).getTime()) / millisecondsPerDay);
}
